<template>
    <!--
        Always-visible filter rail. Sits between the SideNav and main column
        in variant B. Wires every input back to the parent via v-model on
        the `filters` prop (`.sync`-style emit pattern: `update:filters`).
    -->
    <aside :style="rootStyle">
        <header :style="headerStyle">
            <span :style="titleStyle">Filters</span>
            <div :style="actionsStyle">
                <button
                    class="btn btn--ghost btn--xs"
                    :style="clearBtnStyle"
                    type="button"
                    @click="$emit('clear')"
                >Clear</button>
                <button
                    v-if="closable"
                    class="btn btn--icon btn--xs btn--ghost"
                    :style="closeBtnStyle"
                    type="button"
                    aria-label="Close filters"
                    @click="$emit('close')"
                >
                    <Icon name="x" />
                </button>
            </div>
        </header>

        <div :style="bodyStyle">
            <!-- Service (multi-select treeselect — primary filter) -->
            <FilterGroup label="Service" :count="multiCount(filters.service)">
                <Treeselect
                    :value="filters.service"
                    :options="options.service || []"
                    :multiple="true"
                    :value-consists-of="'BRANCH_PRIORITY'"
                    :clear-on-select="true"
                    placeholder="Any service"
                    @input="(v) => updateField('service', v)"
                />
            </FilterGroup>

            <!-- Industry (multi-select treeselect — primary filter) -->
            <FilterGroup label="Industry" :count="multiCount(filters.industry)">
                <Treeselect
                    :value="filters.industry"
                    :options="options.industry || []"
                    :multiple="true"
                    :value-consists-of="'ALL'"
                    :clear-on-select="true"
                    placeholder="Any industry"
                    @input="(v) => updateField('industry', v)"
                />
            </FilterGroup>

            <!-- Name in QuickBooks (text input) -->
            <FilterGroup label="Name in QuickBooks">
                <input
                    type="text"
                    class="input"
                    placeholder="Search QB name…"
                    :style="inputStyle"
                    :value="filters.name_in_quickbooks"
                    @input="updateField('name_in_quickbooks', $event.target.value)"
                />
            </FilterGroup>

            <!-- Client size -->
            <FilterGroup label="Client size">
                <Treeselect
                    :value="filters.client_size"
                    :options="options.client_size || []"
                    :multiple="false"
                    :clear-on-select="true"
                    placeholder="Select…"
                    @input="(v) => updateField('client_size', v)"
                />
            </FilterGroup>

            <!-- International -->
            <FilterGroup label="International">
                <Treeselect
                    :value="filters.international"
                    :options="options.international || []"
                    :multiple="false"
                    :clear-on-select="true"
                    placeholder="Select…"
                    @input="(v) => updateField('international', v)"
                />
            </FilterGroup>

            <!-- Type -->
            <FilterGroup label="Type">
                <Treeselect
                    :value="filters.type"
                    :options="options.type || []"
                    :multiple="false"
                    :clear-on-select="true"
                    placeholder="Select…"
                    @input="(v) => updateField('type', v)"
                />
            </FilterGroup>

            <!-- Language -->
            <FilterGroup label="Language">
                <Treeselect
                    :value="filters.language"
                    :options="options.language || []"
                    :multiple="false"
                    :clear-on-select="true"
                    placeholder="Select…"
                    @input="(v) => updateField('language', v)"
                />
            </FilterGroup>

            <!-- Niche -->
            <FilterGroup label="Niche">
                <Treeselect
                    :value="filters.niche"
                    :options="options.niche || []"
                    :multiple="false"
                    :clear-on-select="true"
                    placeholder="Select…"
                    @input="(v) => updateField('niche', v)"
                />
            </FilterGroup>

            <!-- Skill -->
            <FilterGroup label="Skill">
                <Treeselect
                    :value="filters.skill"
                    :options="options.skill || []"
                    :multiple="false"
                    :clear-on-select="true"
                    placeholder="Select…"
                    @input="(v) => updateField('skill', v)"
                />
            </FilterGroup>

            <!-- Source -->
            <FilterGroup label="Source">
                <Treeselect
                    :value="filters.source"
                    :options="options.source || []"
                    :multiple="false"
                    :clear-on-select="true"
                    placeholder="Select…"
                    @input="(v) => updateField('source', v)"
                />
            </FilterGroup>

            <!-- Budget — boxed radio group + min budget input -->
            <div :style="budgetBoxStyle">
                <div :style="budgetTitleStyle">Budget</div>
                <label :style="radioRowStyle" @click="updateField('budget_type', 'monthly')">
                    <span :style="radioDotStyle(filters.budget_type === 'monthly')">
                        <span
                            v-if="filters.budget_type === 'monthly'"
                            :style="radioInnerStyle"
                        />
                    </span>
                    <span :style="radioLabelStyle(filters.budget_type === 'monthly')">
                        Monthly retainer fee
                    </span>
                </label>
                <label :style="radioRowStyle" @click="updateField('budget_type', 'project')">
                    <span :style="radioDotStyle(filters.budget_type === 'project')">
                        <span
                            v-if="filters.budget_type === 'project'"
                            :style="radioInnerStyle"
                        />
                    </span>
                    <span :style="radioLabelStyle(filters.budget_type === 'project')">
                        Project budget
                    </span>
                </label>
                <input
                    type="text"
                    class="input"
                    placeholder="Min budget…"
                    :style="{ ...inputStyle, marginTop: '8px' }"
                    :value="filters.min_budget"
                    @input="updateField('min_budget', $event.target.value)"
                />
            </div>
        </div>
    </aside>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import Icon from "./Icon.vue";

// One labelled section inside the rail. Optional count badge (red pill)
// shows how many values are active in a multi-select.
const FilterGroup = {
    name: "FilterGroup",
    props: {
        label: { type: String, required: true },
        count: { type: Number, default: null },
    },
    render(h) {
        return h(
            "div",
            {
                style: {
                    padding: "10px 0",
                    borderBottom: "1px solid var(--border-faint)",
                },
            },
            [
                h(
                    "div",
                    {
                        style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "6px",
                        },
                    },
                    [
                        h(
                            "span",
                            {
                                style: {
                                    fontSize: "11.5px",
                                    fontWeight: 500,
                                    color: "var(--text-muted)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                },
                            },
                            this.label
                        ),
                        this.count
                            ? h(
                                "span",
                                {
                                    class: "tag-mono",
                                    style: {
                                        fontSize: "10px",
                                        padding: "1px 6px",
                                        borderRadius: "999px",
                                        background: "var(--tag-red)",
                                        color: "#fff",
                                    },
                                },
                                String(this.count)
                            )
                            : null,
                    ]
                ),
                this.$slots.default,
            ]
        );
    },
};

export default {
    name: "FilterRail",
    components: { FilterGroup, Treeselect, Icon },
    props: {
        filters: { type: Object, required: true },
        options: { type: Object, required: true },
        // "left" = pinned beside the SideNav (variant B). "right" = sheet
        //   on the right edge in variant A — border flips sides and a
        //   close button appears in the header.
        side: { type: String, default: "left", validator: (v) => ["left", "right"].includes(v) },
        // When true, renders an x close button in the header. Wired to
        // @close so the parent can hide the rail.
        closable: { type: Boolean, default: false },
        width: { type: Number, default: 232 },
    },
    computed: {
        rootStyle() {
            const onRight = this.side === "right";
            return {
                width: `${this.width}px`,
                flex: `0 0 ${this.width}px`,
                background: "var(--surface)",
                borderLeft: onRight ? "1px solid var(--border)" : undefined,
                borderRight: onRight ? undefined : "1px solid var(--border)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            };
        },
        closeBtnStyle() {
            return { color: "var(--text-muted)" };
        },
        headerStyle() {
            return {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px 10px",
                borderBottom: "1px solid var(--border-faint)",
            };
        },
        titleStyle() {
            return {
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
            };
        },
        actionsStyle() {
            return { display: "flex", gap: "4px" };
        },
        clearBtnStyle() {
            return { padding: "0 6px", color: "var(--text-muted)" };
        },
        bodyStyle() {
            return {
                flex: "1 1 auto",
                overflow: "auto",
                padding: "8px 14px 14px",
            };
        },
        inputStyle() {
            return { height: "30px", fontSize: "12.5px" };
        },
        budgetBoxStyle() {
            return {
                marginTop: "12px",
                padding: "12px",
                background: "var(--surface-2)",
                border: "1px solid var(--border-faint)",
                borderRadius: "var(--r-md)",
            };
        },
        budgetTitleStyle() {
            return {
                fontSize: "11.5px",
                fontWeight: 500,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: "8px",
            };
        },
        radioRowStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "5px 0",
                cursor: "pointer",
                fontSize: "12.5px",
            };
        },
        radioInnerStyle() {
            return {
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "var(--tag-red)",
            };
        },
    },
    methods: {
        multiCount(value) {
            return Array.isArray(value) && value.length ? value.length : null;
        },
        radioDotStyle(active) {
            return {
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                border: `1.5px solid ${active ? "var(--tag-red)" : "var(--border-strong)"}`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 14px",
            };
        },
        radioLabelStyle(active) {
            return {
                color: active ? "var(--text)" : "var(--text-muted)",
                fontWeight: active ? 500 : 450,
            };
        },
        updateField(key, value) {
            // Single-mutation pattern: emit a clone of filters with the one
            // field changed. Parent v-binds via :filters and listens to
            // update:filters; debounce stays in the parent's onFilterUpdate
            // handler so we don't fire fetchData on every keystroke.
            const next = { ...this.filters, [key]: value };
            this.$emit("update:filters", next);
            this.$emit("change", { key, value, filters: next });
        },
    },
};
</script>
